import { AfterViewChecked, Directive, Input } from '@angular/core';
import { Table } from 'primeng/table';
import { FilterMetadata, FilterOperator } from 'primeng/api';
import { ObjectUtils } from 'primeng/utils';

@Directive({
	standalone: true,
	selector: 'p-table[appCustomTableOverrideFilters]',
})
export class CustomTableOverrideFiltersDirective implements AfterViewChecked {

	@Input() appCustomTableOverrideFilters: {
		resetToFirstPage: boolean,
		scrollToTop: boolean
	} = { resetToFirstPage: true, scrollToTop: true};

	constructor(private table: Table) {
	}

	ngAfterViewChecked() {
		/**
		 * Surcharge de la méthode _filter qui remet systématiquement à la page 1 et qui réinitialise le scroll
		 */
		this.table._filter = () => {
			// Vérifie si l'on doit empêcher le retour à la première page en cas de filtre
			if (!this.table.restoringFilter && this.appCustomTableOverrideFilters?.resetToFirstPage) {
				this.table.first = 0;
				this.table.firstChange.emit(this.table.first);
			}

			if (this.table.lazy) {
				this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
			}
			else {
				if (!this.table.value) {
					return;
				}

				if (!this.table.hasFilter()) {
					this.table.filteredValue = null;
					if (this.table.paginator) {
						this.table.totalRecords = this.table.value ? this.table.value.length : 0;
					}
				}
				else {
					let globalFilterFieldsArray;
					if (this.table.filters['global']) {
						if (!this.table.columns && !this.table.globalFilterFields) {
							throw new Error('Global filtering requires dynamic columns or globalFilterFields to be defined.');
						} else {
							globalFilterFieldsArray = this.table.globalFilterFields || this.table.columns;
						}
					}

					this.table.filteredValue = [];

					for (let i = 0; i < this.table.value.length; i++) {
						let localMatch = true;
						let globalMatch = false;
						let localFiltered = false;

						for (const prop in this.table.filters) {
							if (this.table.filters.hasOwnProperty(prop) && prop !== 'global') {
								localFiltered = true;
								const filterField = prop;
								const filterMeta = this.table.filters[filterField];

								if (Array.isArray(filterMeta)) {
									for (const meta of filterMeta) {
										localMatch = this.table.executeLocalFilter(filterField, this.table.value[i], meta);

										if ((meta.operator === FilterOperator.OR && localMatch) || (meta.operator === FilterOperator.AND && !localMatch)) {
											break;
										}
									}
								}
								else {
									localMatch = this.table.executeLocalFilter(filterField, this.table.value[i], <any>filterMeta);
								}

								if (!localMatch) {
									break;
								}
							}
						}

						if (this.table.filters['global'] && !globalMatch && globalFilterFieldsArray) {
							for (let j = 0; j < globalFilterFieldsArray.length; j++) {
								const globalFilterField = globalFilterFieldsArray[j].field || globalFilterFieldsArray[j];
								globalMatch = (<any>this.table.filterService).filters[(<any>this.table.filters['global']).matchMode](ObjectUtils.resolveFieldData(this.table.value[i], globalFilterField), (<FilterMetadata>this.table.filters['global']).value, this.table.filterLocale);

								if (globalMatch) {
									break;
								}
							}
						}

						let matches: boolean;
						if (this.table.filters['global']) {
							matches = localFiltered ? (localFiltered && localMatch && globalMatch) : globalMatch;
						}
						else {
							matches = localFiltered && localMatch;
						}

						if (matches) {
							this.table.filteredValue.push(this.table.value[i]);
						}
					}

					if (this.table.filteredValue.length === this.table.value.length) {
						this.table.filteredValue = null;
					}

					if (this.table.paginator) {
						this.table.totalRecords = this.table.filteredValue ? this.table.filteredValue.length : this.table.value ? this.table.value.length : 0;
					}
				}
			}

			this.table.onFilter.emit({
				filters: <{ [s: string]: FilterMetadata | undefined }>this.table.filters,
				filteredValue: this.table.filteredValue || this.table.value
			});

			this.table.tableService.onValueChange(this.table.value);

			if (this.table.isStateful() && !this.table.restoringFilter) {
				this.table.saveState();
			}

			if (this.table.restoringFilter) {
				this.table.restoringFilter = false;
			}

			this.table.cd.markForCheck();

			// Vérifie si l'on doit empêcher de reset le scroll en haut du tableau
			if (this.table.scrollable && this.appCustomTableOverrideFilters.scrollToTop) {
				this.table.resetScrollTop();
			}
		};
	}
}
